import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Save, Tag, FileText, Loader } from 'lucide-react';
import { toast } from 'react-toastify';
import { useLanguage } from '../../contexts/LanguageContext';
import { handleApiError, notesAPI } from '../../utils/api';

const CreateNoteModal = ({ isOpen, onClose, note = null, onSuccess }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    tags: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Pré-remplir le formulaire si on modifie une note
  useEffect(() => {
    if (note) {
      setFormData({
        title: note.title || '',
        content: note.content || '',
        tags: note.tags || ''
      });
    } else {
      setFormData({
        title: '',
        content: '',
        tags: ''
      });
    }
    setErrors({});
  }, [note, isOpen]);

  // Validation du formulaire
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = t('notes.modal.errors.titleRequired');
    } else if (formData.title.length > 200) {
      newErrors.title = t('notes.modal.errors.titleTooLong');
    }
    
    if (!formData.content.trim()) {
      newErrors.content = t('notes.modal.errors.contentRequired');
    } else if (formData.content.length > 5000) {
      newErrors.content = t('notes.modal.errors.contentTooLong');
    }
    
    if (formData.tags && formData.tags.length > 500) {
      newErrors.tags = t('notes.modal.errors.tagsTooLong');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Sauvegarder la note
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const response = note
        ? await notesAPI.update(note.id, formData)
        : await notesAPI.create(formData);

      const data = response.data;
      toast.success(note ? t('notes.modal.messages.updateSuccess') : t('notes.modal.messages.createSuccess'));
      
      // Appeler onSuccess avant de fermer le modal
      if (onSuccess) {
        await onSuccess(data.note);
      }
      
      onClose();
    } catch (error) {
      console.error('Error saving note:', error);
      toast.error(handleApiError(error, t('notes.modal.errors.saveErrorGeneral')));
    } finally {
      setLoading(false);
    }
  };

  // Gérer les changements de formulaire
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Effacer l'erreur du champ modifié
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Fermer le modal
  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-white rounded-lg border border-gray-200 shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <FileText className="w-5 h-5 text-indigo-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">
              {note ? t('notes.modal.editTitle') : t('notes.modal.createTitle')}
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
            aria-label={t('common.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-4">
            {/* Titre */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('notes.modal.titleLabel')}
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder={t('notes.modal.titlePlaceholder')}
                maxLength={200}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors ${
                  errors.title ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                disabled={loading}
              />
              {errors.title && (
                <p className="text-sm text-red-600 mt-1">{errors.title}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                {formData.title.length}/200 {t('notes.modal.charactersLabel')}
              </p>
            </div>

            {/* Contenu */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('notes.modal.contentLabel')}
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => handleChange('content', e.target.value)}
                placeholder={t('notes.modal.contentPlaceholder')}
                rows={8}
                maxLength={5000}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors resize-none ${
                  errors.content ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                disabled={loading}
              />
              {errors.content && (
                <p className="text-sm text-red-600 mt-1">{errors.content}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                {formData.content.length}/5000 {t('notes.modal.charactersLabel')}
              </p>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Tag className="h-4 w-4 inline mr-1" />
                {t('notes.modal.tagsLabel')}
              </label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => handleChange('tags', e.target.value)}
                placeholder={t('notes.modal.tagsPlaceholder')}
                maxLength={500}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors ${
                  errors.tags ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                disabled={loading}
              />
              {errors.tags && (
                <p className="text-sm text-red-600 mt-1">{errors.tags}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                {t('notes.modal.tagsHelp')} • {formData.tags.length}/500 {t('notes.modal.charactersLabel')}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors disabled:opacity-50"
          >
{t('notes.modal.cancelButton')}
          </button>
          <button
            onClick={handleSave}
            disabled={loading || !formData.title.trim() || !formData.content.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {loading ? (
              <>
                <Loader className="h-4 w-4 animate-spin mr-2" />
{t('notes.modal.savingLabel')}
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
{note ? t('notes.modal.updateButton') : t('notes.modal.createButton')}
              </>
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default CreateNoteModal;
